import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Shield, Award, Globe, CheckCircle, Users, Building2, Factory, Leaf, FileCheck, BookOpen, Phone, Star, Quote, Target, ShieldCheck } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { SEO } from "../components/SEO";
import { SelfPacedBadge } from "../components/SelfPacedBadge";
import { UpcomingTrainingSection } from "../components/UpcomingTrainingSection";
import { TESTIMONIALS as testimonials } from "../data/testimonials";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Home = () => {
  const [featuredCourses, setFeaturedCourses] = useState([]);

  useEffect(() => {
    const fetchCourses = async () => {
      try {
        await axios.post(`${API}/seed`);
        const response = await axios.get(`${API}/courses?limit=6`);
        setFeaturedCourses(response.data);
      } catch (error) {
        console.error("Error fetching courses:", error);
      }
    };
    fetchCourses();
  }, []);

  const services = [
    {
      icon: ShieldCheck,
      title: "ISO 9001",
      subtitle: "Quality Management",
      description: "Build a world-class quality management system that drives customer satisfaction and operational excellence.",
      link: "/services/iso-9001"
    },
    {
      icon: FileCheck,
      title: "ISO 22000 / FSSC 22000",
      subtitle: "Food Safety",
      description: "Achieve GFSI-recognized food safety certification with comprehensive FSMS implementation.",
      link: "/services/iso-22000"
    },
    {
      icon: Shield,
      title: "HACCP & GMP",
      subtitle: "Food Safety Controls",
      description: "Implement proven hazard analysis and good manufacturing practices to protect consumers.",
      link: "/services/haccp-gmp"
    },
    {
      icon: Leaf,
      title: "GAP / GlobalGAP",
      subtitle: "Agricultural Practices",
      description: "Demonstrate responsible agricultural practices and access premium global markets.",
      link: "/services/gap-globalgap"
    },
    {
      icon: Award,
      title: "SQF Certification",
      subtitle: "Safe Quality Food",
      description: "Achieve GFSI-benchmarked food safety and quality certification for North American markets.",
      link: "/services/sqf"
    },
    {
      icon: Target,
      title: "Auditing Services",
      subtitle: "Internal & Supplier",
      description: "Comprehensive audits that verify compliance and drive continuous improvement.",
      link: "/services/auditing"
    },
    {
      icon: Shield,
      title: "ISO 31000",
      subtitle: "Risk Management",
      description: "Build organizational resilience through structured enterprise risk management.",
      link: "/services/iso-31000"
    },
    {
      icon: Globe,
      title: "Market Access",
      subtitle: "Export Compliance",
      description: "Navigate complex export requirements for USA, Canada, EU, and UK markets.",
      link: "/services/market-access"
    }
  ];

  const industries = [
    { icon: Factory, name: "Food Manufacturing", description: "GFSI standards, HACCP, food safety systems" },
    { icon: Leaf, name: "Agri-Business", description: "GAP, GlobalGAP, agricultural compliance" },
    { icon: Building2, name: "Service & Government", description: "Quality management, process optimization" },
    { icon: Users, name: "SMEs", description: "Tailored solutions for growing businesses" },
  ];

  const stats = [
    { value: "15+", label: "Years Experience" },
    { value: "500+", label: "Clients Served" },
    { value: "38", label: "Training Courses" },
    { value: "100%", label: "Client Satisfaction" },
  ];


  return (
    <div className="min-h-screen">
      <SEO
        title="Compliance Management Systems | Quality & Food Safety Consultants"
        description="Expert compliance consulting, auditing, and PECB certified training. ISO 9001, ISO 22000, FSSC 22000, HACCP implementation. Serving the USA, Jamaica, the Caribbean & internationally."
        url="/"
        keywords={["ISO consultant USA", "ISO training Madison Mississippi", "compliance consulting Jamaica", "ISO certification Caribbean", "food safety consultant", "PECB training", "quality management systems"]}
      />

      {/* Hero Section */}
      <section className="relative bg-navy-darker text-white overflow-hidden">
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: "url('https://images.pexels.com/photos/3184291/pexels-photo-3184291.jpeg')",
            backgroundSize: "cover",
            backgroundPosition: "center"
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-navy-darker via-navy-darker/95 to-navy-darker/80" />
        
        <div className="relative container mx-auto px-4 lg:px-8 py-24 lg:py-32">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-gold/20 text-gold px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Award className="h-4 w-4" />
              PECB Authorized Partner
            </div>
            <h1 className="font-heading font-bold text-4xl lg:text-6xl leading-tight mb-6">
              Achieve Compliance <br />
              <span className="text-gold">With Confidence</span>
            </h1>
            <p className="text-lg lg:text-xl text-slate-300 mb-8 leading-relaxed">
              At CMS, we transcend borders as a distinguished global consultancy. Specializing in quality management, food safety, and process optimization, our mission is to empower organizations to achieve unparalleled excellence.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/services/consulting">
                <Button size="lg" className="bg-gold hover:bg-gold-dark text-navy-darker font-semibold">
                  Request Consulting
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/courses">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                  View Training Options
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Bar */}
      <section className="py-8 bg-slate-50 border-y border-slate-200">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="flex flex-wrap items-center justify-center gap-8 lg:gap-16">
            {/* PECB Authorized Partner Badge - Prominent */}
            <div className="flex items-center bg-white px-6 py-3 rounded-xl shadow-md border-2 border-brand/20">
              <img 
                src="https://customer-assets.emergentagent.com/job_training-comply/artifacts/a4vpv6m5_1-pecb-authorized-reseller-initiate.png" 
                alt="PECB Authorized Partner" 
                className="h-20 w-auto"
              />
              <div className="ml-4 border-l-2 border-brand/30 pl-4">
                <p className="font-heading font-bold text-brand text-lg">Authorized</p>
                <p className="font-heading font-semibold text-charcoal text-sm">Training Partner</p>
              </div>
            </div>
            <div className="flex items-center space-x-2 text-slate-600">
              <Shield className="h-7 w-7 text-brand" />
              <span className="font-heading font-semibold">ISO Consultants</span>
            </div>
            <div className="flex items-center space-x-2 text-slate-600">
              <Globe className="h-7 w-7 text-brand" />
              <span className="font-heading font-semibold">Global Recognition</span>
            </div>
            <div className="flex items-center space-x-2 text-slate-600">
              <CheckCircle className="h-7 w-7 text-brand" />
              <span className="font-heading font-semibold">Accredited Training</span>
            </div>
          </div>
        </div>
      </section>

      <UpcomingTrainingSection />

      {/* Services Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="font-heading font-bold text-3xl lg:text-4xl text-primary mb-4">
              Our Services
            </h2>
            <p className="text-lg text-muted-foreground">
              Comprehensive compliance consulting tailored to your industry. Click on any service to learn more.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, index) => (
              <Link key={index} to={service.link} className="block group">
                <Card className="h-full hover:shadow-xl transition-all duration-300 border-2 hover:border-brand/30 group-hover:-translate-y-1">
                  <CardContent className="p-6">
                    <div className="w-12 h-12 bg-brand/10 rounded-xl flex items-center justify-center mb-4 group-hover:bg-brand group-hover:text-white transition-colors">
                      <service.icon className="h-6 w-6 text-brand group-hover:text-white" />
                    </div>
                    <h3 className="font-heading font-bold text-lg mb-1">{service.title}</h3>
                    <p className="text-xs text-brand font-medium mb-2">{service.subtitle}</p>
                    <p className="text-sm text-muted-foreground mb-4 line-clamp-2">{service.description}</p>
                    <span className="inline-flex items-center text-sm text-brand font-medium group-hover:text-brand-dark">
                      Learn More <ArrowRight className="ml-1 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                    </span>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>

          <div className="text-center mt-10">
            <Link to="/services/consulting">
              <Button size="lg" variant="outline" className="border-brand text-brand hover:bg-brand hover:text-white">
                Request a Consultation
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Industries Section */}
      <section className="py-20 bg-navy-darker text-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="font-heading font-bold text-3xl lg:text-4xl mb-4">
              Industries We Serve
            </h2>
            <p className="text-lg text-slate-300">
              Your trusted partner for quality, safety, and success across diverse sectors
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {industries.map((industry, index) => (
              <div key={index} className="bg-white/5 backdrop-blur-sm rounded-xl p-6 hover:bg-white/10 transition-colors">
                <industry.icon className="h-10 w-10 text-gold mb-4" />
                <h3 className="font-heading font-semibold text-lg mb-2">{industry.name}</h3>
                <p className="text-slate-400 text-sm">{industry.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-brand">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center text-white">
                <div className="font-heading font-bold text-4xl lg:text-5xl mb-2">{stat.value}</div>
                <div className="text-white/80">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="font-heading font-bold text-3xl lg:text-4xl text-primary mb-4">
              What Our Clients Say
            </h2>
            <p className="text-lg text-muted-foreground">
              Trusted by leading organizations across the United States, Jamaica and the Caribbean
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <Card key={index} className="relative bg-slate-50 border-0 hover:shadow-lg transition-shadow">
                <CardContent className="p-8">
                  {/* Quote Icon */}
                  <div className="absolute -top-4 left-8">
                    <div className="w-10 h-10 bg-brand rounded-full flex items-center justify-center">
                      <Quote className="h-5 w-5 text-white" />
                    </div>
                  </div>
                  
                  {/* Rating Stars */}
                  <div className="flex gap-1 mb-4 pt-2">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="h-4 w-4 fill-brand text-brand" />
                    ))}
                  </div>
                  
                  {/* Quote */}
                  <p className="text-slate-700 mb-6 leading-relaxed italic">
                    "{testimonial.quote}"
                  </p>
                  
                  {/* Author */}
                  <div className="border-t border-slate-200 pt-4">
                    <p className="font-heading font-semibold text-primary">{testimonial.author}</p>
                    <p className="text-sm text-muted-foreground">{testimonial.role}</p>
                    <p className="text-sm text-brand font-medium">{testimonial.company}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Courses */}
      {featuredCourses.length > 0 && (
        <section className="py-20 bg-slate-50">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between mb-12">
              <div>
                <h2 className="font-heading font-bold text-3xl lg:text-4xl text-primary mb-4">
                  PECB Training Courses
                </h2>
                <p className="text-lg text-muted-foreground max-w-2xl">
                  Globally accredited courses to enhance your professional expertise
                </p>
              </div>
              <Link to="/courses" className="mt-4 lg:mt-0">
                <Button variant="outline" className="border-brand text-brand hover:bg-brand hover:text-white">
                  View All Courses
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {featuredCourses.slice(0, 6).map((course) => (
                <Card key={course.id} className="hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex flex-wrap items-center gap-2 mb-3">
                      <span className="bg-brand/10 text-brand text-xs font-medium px-2 py-1 rounded">
                        {course.category}
                      </span>
                      <span className="bg-gold/10 text-gold-dark text-xs font-medium px-2 py-1 rounded">
                        {course.certification_level}
                      </span>
                      <SelfPacedBadge course={course} />
                    </div>
                    <h3 className="font-heading font-semibold text-lg mb-2 line-clamp-2">{course.title}</h3>
                    <p className="text-sm text-muted-foreground mb-4 line-clamp-2">{course.description}</p>
                    <div className="flex items-center justify-between">
                      <span className="font-heading font-bold text-brand">
                        From ${Math.min(...course.delivery_formats.map(f => f.price))}
                      </span>
                      <Link to={`/courses/${course.id}${course.cohorts?.length ? "?format=online-live" : ""}`}>
                        <Button size="sm" variant="outline" className="text-brand border-brand hover:bg-brand hover:text-white">
                          View Details
                        </Button>
                      </Link>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="bg-navy-darker rounded-3xl p-8 lg:p-16 text-center text-white">
            <h2 className="font-heading font-bold text-3xl lg:text-4xl mb-4">
              Ready to Achieve Excellence?
            </h2>
            <p className="text-lg text-slate-300 max-w-2xl mx-auto mb-8">
              Let's discuss how we can help your organization meet and exceed compliance standards.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/contact">
                <Button size="lg" className="bg-gold hover:bg-gold-dark text-navy-darker font-semibold">
                  <Phone className="mr-2 h-5 w-5" />
                  Contact Us Today
                </Button>
              </Link>
              <Link to="/services/consulting">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                  Explore Our Services
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
